import styled from 'styled-components';
import { midiToNoteName, voicePartName, voicePartSentence } from '../../../../../common/src/learn_rules.ts';
import { VocalRange, VoicePart } from '../../../../../common/src/model.ts';
import { ButtonColumn, PrimaryButton, SecondaryButton } from '../learn_buttons.tsx';
import { ScreenBody, shortScreen, Slack } from '../learn_screen_parts.tsx';
import { IllustrationCircle } from './range_screen_parts.tsx';

export const rangeResultScreenId = 'range-result';
export const rangeStartPitchButtonId = 'range-start-pitch-button';
export const rangeBackButtonId = 'range-back-button';
export const rangePartBandsTestId = 'range-part-bands';
export const rangePartRowTestId = 'range-part-row';
export const rangeResultPartTestId = 'range-result-part';

// The assumed comfortable range of each part (the numbers learn_rules.ts documents), drawn as the
// light bands the singer's measured range is compared with
export const assumedPartRanges: Array<{ part: VoicePart; range: VocalRange }> = [
  { part: VoicePart.Soprano, range: { lowMidi: 60, highMidi: 81 } }, // C4 to A5
  { part: VoicePart.Alto, range: { lowMidi: 55, highMidi: 76 } }, // G3 to E5
  { part: VoicePart.Tenor, range: { lowMidi: 48, highMidi: 69 } }, // C3 to A4
  { part: VoicePart.Bass, range: { lowMidi: 40, highMidi: 64 } }, // E2 to E4
];

// The bands' axis: C2 to C6 covers every part with a little room past the bass and soprano ends
export const partBandsAxis: VocalRange = { lowMidi: 36, highMidi: 84 };

// The result of the Range Finder (issues #3481, #3510), from the "Range result" artboard plus the
// part decided on 2026-09-10: the check, the part sentence as the headline, "Your comfortable
// range" under it, the part bands with the singer's range named on its bar, and the two buttons.
// The title sits below the headline and close above the bands because it labels them, not the
// part. The formatted range line and the explanation paragraph are gone (#3510): the bar says the
// range once. Both buttons are the page's to name: the way on leads to the Pitch class screen
// ("Continue to Pitch") or, for a singer who came from it, into Pitch Match ("Start Pitch Match"),
// and the way back returns wherever the singer came from ("Back to Learn", "Back to Pitch",
// "Back"), the Range Finder belonging to no class. Presentational.
export const RangeResultScreen = ({
  range,
  part,
  nextLabel,
  onNext,
  backLabel,
  onBack,
}: {
  range: VocalRange;
  part: VoicePart;
  nextLabel: string;
  onNext: () => void;
  backLabel: string;
  onBack: () => void;
}) => (
  <ScreenBody id={rangeResultScreenId}>
    <Centered>
      <IllustrationCircle>
        <CheckMark aria-hidden="true" viewBox="0 0 24 24">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </CheckMark>
      </IllustrationCircle>
      <PartHeadline data-testid={rangeResultPartTestId}>{voicePartSentence(part)}!</PartHeadline>
      <ResultTitle>Your comfortable range</ResultTitle>
    </Centered>
    <RangePartBands range={range} part={part} />
    <Slack />
    <ButtonColumn>
      <PrimaryButton id={rangeStartPitchButtonId} data-testid={rangeStartPitchButtonId} onClick={onNext}>
        {nextLabel}
      </PrimaryButton>
      <SecondaryButton id={rangeBackButtonId} data-testid={rangeBackButtonId} onClick={onBack}>
        {backLabel}
      </SecondaryButton>
    </ButtonColumn>
  </ScreenBody>
);

// Where a note sits on the axis, as a percentage from the left
function axisPercent(midi: number): number {
  const span = partBandsAxis.highMidi - partBandsAxis.lowMidi;
  const clamped = Math.min(partBandsAxis.highMidi, Math.max(partBandsAxis.lowMidi, midi));
  return ((clamped - partBandsAxis.lowMidi) / span) * 100;
}

function bandStyle(range: VocalRange) {
  const left = axisPercent(range.lowMidi);
  const right = axisPercent(range.highMidi);
  return { left: `${left}%`, width: `${right - left}%` };
}

// Four rows, one per part, on one axis: each part's assumed range as a light band, the singer's
// row labelled in the accent colour, and the singer's measured range as a solid bar on that row
// with its ends named
export const RangePartBands = ({ range, part }: { range: VocalRange; part: VoicePart }) => (
  <Bands data-testid={rangePartBandsTestId}>
    {assumedPartRanges.map(({ part: rowPart, range: assumed }) => {
      const singer = rowPart === part;
      return (
        <PartRow
          key={rowPart}
          data-testid={rangePartRowTestId}
          data-part={rowPart}
          data-singer={singer ? 'true' : 'false'}
        >
          <PartLabel $singer={singer}>{voicePartName(rowPart)}</PartLabel>
          <TrackArea>
            <Track>
              <AssumedBand style={bandStyle(assumed)} />
              {singer && <SingerBar style={bandStyle(range)} />}
            </Track>
            {singer && (
              <EndLabels>
                <EndLabel style={{ left: `${axisPercent(range.lowMidi)}%` }}>{midiToNoteName(range.lowMidi)}</EndLabel>
                <EndLabel style={{ left: `${axisPercent(range.highMidi)}%` }}>
                  {midiToNoteName(range.highMidi)}
                </EndLabel>
              </EndLabels>
            )}
          </TrackArea>
        </PartRow>
      );
    })}
  </Bands>
);

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  text-align: center;

  ${shortScreen} {
    gap: 4px;
    margin-top: 6px;
  }
`;

const CheckMark = styled.svg`
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const ResultTitle = styled.div`
  margin-top: 4px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 24px;
  line-height: 32px;

  ${shortScreen} {
    margin-top: 2px;
    font-size: 22px;
    line-height: 28px;
  }
`;

// The part is the headline ("You are a bass!"); the bands below name the range
const PartHeadline = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 40px;
  line-height: 1.1;
  color: var(--color-tab-selected);

  ${shortScreen} {
    font-size: 34px;
  }
`;

// Closer to the title above them than the title is to the headline, so "Your comfortable range"
// reads as these bands' label rather than as a second line about the part
const Bands = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
  padding: 0 8px;

  ${shortScreen} {
    gap: 4px;
    margin-top: 4px;
  }
`;

const partRowHeight = 32; // the track plus the end labels under the singer's bar

const PartRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  height: ${partRowHeight}px;
`;

const PartLabel = styled.div<{ $singer: boolean }>`
  flex: none;
  width: 56px;
  font-size: 13px;
  line-height: 14px;
  font-family: ${({ $singer }) => ($singer ? 'Jost-SemiBold' : 'Jost')}, Arial, sans-serif;
  color: ${({ $singer }) => ($singer ? 'var(--color-tab-selected)' : 'var(--color-text-darker)')};
`;

const TrackArea = styled.div`
  flex: auto;
  position: relative;
`;

const Track = styled.div`
  position: relative;
  height: 14px;
  border-radius: 7px;
  background-color: var(--color-background-search);
`;

const AssumedBand = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 7px;
  background-color: color-mix(in srgb, var(--color-tab-selected) 22%, transparent);
`;

const SingerBar = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 6px;
  background-color: var(--color-tab-selected);
`;

const EndLabels = styled.div`
  position: relative;
  height: ${partRowHeight - 14}px;
`;

const EndLabel = styled.div`
  position: absolute;
  top: 2px;
  transform: translateX(-50%);
  font-size: 12px;
  line-height: 14px;
  color: var(--color-tab-selected);
  white-space: nowrap;
`;
